import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import type { GridApi } from "ag-grid-community";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import {
	useBarrels,
	useMillSizes,
	useRollsetsList,
	useSetRollsetStatus,
	useStands,
} from "../hooks/useRollshopOperations";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	ChangeRollPositionModal,
	type RollPosition,
} from "./ChangeRollPositionModal";
import { NewRollsetModal } from "./NewRollsetModal";
import { computeAssignmentStatus, RollsetsTable } from "./RollsetsTable";
import { RollsetDetailPanel } from "./RollsetDetailPanel";
import { RollsetsToolbar } from "./RollsetsToolbar";

// Master on the LEFT, detail on the RIGHT — Mario's preferred layout for
// the Rollshop Operations pages (the table is the entry point, detail is
// supplementary on the side).
const DEFAULT_LAYOUT = { left: 62, right: 38 };

const matchesSearch = (row: RollsetListItem, q: string): boolean => {
	if (!q) return true;
	const needle = q.toLowerCase();
	return (
		(row.Name?.toLowerCase().includes(needle) ?? false) ||
		(row.TopRollName?.toLowerCase().includes(needle) ?? false) ||
		(row.BottomRollName?.toLowerCase().includes(needle) ?? false) ||
		(row.BarrelName?.toLowerCase().includes(needle) ?? false)
	);
};

interface PositionContext {
	position: RollPosition;
	currentRollId: number | null;
	currentRollName: string | null;
}

const URL_SCHEMA = {
	search: urlParam.string(),
	statusCode: urlParam.string(),
	millSizeId: urlParam.int(),
	standId: urlParam.int(),
	barrelId: urlParam.int(),
	rollsetId: urlParam.int(),
};

export const Rollsets = () => {
	const writesLocked = useOperationalWritesLocked();
	const [
		{ search, statusCode, millSizeId, standId, barrelId, rollsetId },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [errorMsg, setErrorMsg] = useState("");
	const [notification, setNotification] = useState("");
	const [newOpen, setNewOpen] = useState(false);
	const [posCtx, setPosCtx] = useState<PositionContext | null>(null);

	const { data, isFetching, isSuccess, error, refetch } = useRollsetsList();
	const millSizesQuery = useMillSizes();
	const standsQuery = useStands();
	const barrelsQuery = useBarrels();
	const gridApiRef = useRef<GridApi<RollsetListItem> | null>(null);

	const { setStatus, isSettingStatus } = useSetRollsetStatus({
		onSuccess: () => setNotification("Rollset status updated"),
		onError: (e) => setErrorMsg(e.message),
	});

	// Lookup maps used by the table to resolve StandId → StandName and
	// MillSizeId → MillSize Name (the SP returns only the IDs; the legacy
	// frontend does the same lookup against the catalogs).
	const standsByIdName = useMemo(() => {
		const m = new Map<number, string>();
		for (const s of standsQuery.data ?? []) {
			if (s.Id != null) m.set(s.Id, s.Name);
		}
		return m;
	}, [standsQuery.data]);

	const millSizesByIdName = useMemo(() => {
		const m = new Map<number, string>();
		for (const ms of millSizesQuery.data ?? []) {
			if (ms.Id != null) m.set(ms.Id, ms.Name);
		}
		return m;
	}, [millSizesQuery.data]);

	const filteredRows = useMemo(() => {
		if (!data) return [];
		return data.filter(
			(row) =>
				matchesSearch(row, searchText) &&
				(statusCode == null || row.StatusCode === statusCode) &&
				(millSizeId == null || row.MillSizeId === millSizeId) &&
				(standId == null || row.StandId === standId) &&
				(barrelId == null || row.BarrelId === barrelId),
		);
	}, [data, searchText, statusCode, millSizeId, standId, barrelId]);

	// The selection is the id in the URL, resolved against the rollsets the server returned
	// rather than the filtered view, so narrowing the grid does not empty the panel.
	const syncedSelected = useMemo(
		() =>
			rollsetId === null
				? null
				: ((data ?? []).find((r) => r.Id === rollsetId) ?? null),
		[data, rollsetId],
	);

	// A link to a rollset that no longer exists would leave the panel empty, so the key goes.
	useEffect(() => {
		if (!isSuccess || rollsetId === null) return;
		if (!(data ?? []).some((r) => r.Id === rollsetId)) dropValues("rollsetId");
	}, [isSuccess, data, rollsetId, dropValues]);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "rollshop-operations-rollsets-v2",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const handleRefresh = useCallback(() => {
		refetch();
	}, [refetch]);

	const handleAdd = useCallback(() => {
		setNewOpen(true);
	}, []);

	const handleSetStatus = useCallback(
		(code: string) => {
			if (rollsetId !== null) {
				setStatus({ id: rollsetId, statusCode: code });
			}
		},
		[rollsetId, setStatus],
	);

	const handleChangeRollPosition = useCallback(
		(
			position: RollPosition,
			currentRollId: number | null,
			currentRollName: string | null,
		) => {
			setPosCtx({ position, currentRollId, currentRollName });
		},
		[],
	);

	// Computed columns (Size, Stand, AssignmentStatus, RollsFullName) need
	// explicit overrides in the export — AG Grid only auto-resolves `field`
	// columns, not `valueGetter`-only columns.
	const handleExportExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Rollsets.xlsx",
			processCellCallback: (p) => {
				const colId = p.column.getColId();
				const data = p.node?.data as RollsetListItem | undefined;
				if (!data) return p.value ?? "";
				if (colId === "RollsFullName") {
					return `${data.TopRollName ?? "-"} - ${data.BottomRollName ?? "-"}`;
				}
				if (colId === "Size") {
					return data.MillSizeId != null
						? (millSizesByIdName.get(data.MillSizeId) ?? "")
						: "";
				}
				if (colId === "Stand") {
					return data.StandId != null
						? (standsByIdName.get(data.StandId) ?? "")
						: "";
				}
				if (colId === "AssignmentStatus") {
					const code = computeAssignmentStatus(data);
					return code === "ASSIGNED"
						? "Assigned"
						: code === "PARTIAL"
							? "Partial"
							: "Available";
				}
				return p.value ?? "";
			},
		});
	}, [millSizesByIdName, standsByIdName]);

	if (error && !errorMsg) {
		setErrorMsg(
			error instanceof Error ? error.message : "Error fetching rollsets",
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				gap: 1,
			}}
		>
			<Paper elevation={3}>
				<RollsetsToolbar
					search={searchText}
					onSearchChange={setSearchText}
					statusCode={statusCode}
					onStatusChange={(code) => setFilters({ statusCode: code })}
					millSizeId={millSizeId}
					onMillSizeChange={(id) => setFilters({ millSizeId: id })}
					standId={standId}
					onStandChange={(id) => setFilters({ standId: id })}
					barrelId={barrelId}
					onBarrelChange={(id) => setFilters({ barrelId: id })}
					millSizes={millSizesQuery.data ?? []}
					stands={standsQuery.data ?? []}
					barrels={barrelsQuery.data ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportExcel}
					onAdd={handleAdd}
					addDisabled={writesLocked}
					addDisabledReason={OPERATIONAL_WRITES_LOCKED_MESSAGE}
					isLoading={isFetching}
				/>
			</Paper>

			<Group
				id="rollsets-master-detail"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<RollsetsTable
								rows={filteredRows}
								selectedId={syncedSelected?.Id ?? null}
								onRowClick={(row) => setFilters({ rollsetId: row.Id })}
								isLoading={isFetching}
								standsByIdName={standsByIdName}
								millSizesByIdName={millSizesByIdName}
								onGridReady={(api) => {
									gridApiRef.current = api;
								}}
							/>
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={20} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pl: 1 }}>
						<Paper
							elevation={3}
							sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}
						>
							<RollsetDetailPanel
								rollset={syncedSelected}
								disabled={writesLocked}
								disabledReason={OPERATIONAL_WRITES_LOCKED_MESSAGE}
								onChangeRollPosition={handleChangeRollPosition}
								onSetStatus={handleSetStatus}
								isSettingStatus={isSettingStatus}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<NewRollsetModal
				open={newOpen}
				onClose={() => setNewOpen(false)}
				onCreated={(id) => {
					setNotification("Rollset created successfully");
					// The panel fills in once the list refetch brings the new rollset.
					setFilters({ rollsetId: id });
				}}
			/>

			<ChangeRollPositionModal
				open={posCtx !== null}
				rollset={syncedSelected}
				position={posCtx?.position ?? null}
				currentRollId={posCtx?.currentRollId ?? null}
				currentRollName={posCtx?.currentRollName ?? null}
				onClose={() => setPosCtx(null)}
				onSuccess={() => {
					setPosCtx(null);
					setNotification("Roll updated");
				}}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
